import { EChartsOption } from 'echarts';
import * as echarts from 'echarts/core';
import {
  ChartDataSeries,
  dataViewFormatter,
  decimalScoreCellRenderer,
  getTimestampedFileNameForWidget,
  RowData,
} from '../helpers/widgets';

interface RiparianFeaturesData {
  riparianCoverageText: {
    value: number;
    name: string;
  };
  taongaSpeciesAbundance: {
    value: number;
    name: string;
  };
  taongaWaiSpeciesAbundance: {
    value: number;
    name: string;
  };
  rongoaSpeciesAbundance: {
    value: number;
    name: string;
  };
}

interface RiparianFeaturesDataByDate {
  [date: string]: RiparianFeaturesData;
}

/**
 * Should be 5 colours max, but just in case an extra 3
 * selected by rank attribute
 */
const RiparianFeaturesColours: { [key: string]: { primary: string; light: string } } = {
  riparianCoverageText: {
    primary: '#00916f',
    light: '#86cdae',
  },
  taonga: {
    primary: '#473b7b',
    light: '#7c70a1',
  },
  taongaWai: {
    primary: '#c83c7b',
    light: '#ee4891',
  },
  rongoa: {
    primary: '#f26b4b',
    light: '#f79a81',
  },
};

const getRiparianFeaturesChartConfig = (data: RiparianFeaturesDataByDate): EChartsOption => {
  // Prepare x-axis and series data
  const xAxisDates = Object.keys(data);
  const riparianCoverageValues = xAxisDates.map((date) => data[date].riparianCoverageText.value);
  const taongaSpeciesAbundanceValues = xAxisDates.map((date) => data[date].taongaSpeciesAbundance.value);
  const taongaWaiSpeciesAbundanceValues = xAxisDates.map((date) => data[date].taongaWaiSpeciesAbundance.value);
  const rongoaSpeciesAbundanceValues = xAxisDates.map((date) => data[date].rongoaSpeciesAbundance.value);

  const cfg: EChartsOption = {
    color: [
      RiparianFeaturesColours.riparianCoverageText.primary,
      RiparianFeaturesColours.taonga.primary,
      RiparianFeaturesColours.taongaWai.primary,
      RiparianFeaturesColours.rongoa.primary,
    ],
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'none',
      },
      formatter: (params: any) => {
        const date = params[0].axisValue;
        const quality = data[date];

        let tooltipHtml = '';

        if (quality) {
          tooltipHtml += `<em>Riparian Coverage</em>: <strong>${quality.riparianCoverageText.name}</strong><br/>`;
          tooltipHtml += `<em>Taonga Abundance</em>: <strong>${quality.taongaSpeciesAbundance.name}</strong><br/>`;
          tooltipHtml += `<em>Taonga Wai Abundance</em>: <strong>${quality.taongaWaiSpeciesAbundance.name}</strong><br/>`;
          tooltipHtml += `<em>Rongoa Abundance</em>: <strong>${quality.rongoaSpeciesAbundance.name}</strong><br/>`;
        }

        return tooltipHtml;
      },
    },
    legend: {
      data: ['Riparian Coverage', 'Taonga Abundance', 'Taonga Wai Abundance', 'Rongoa Abundance'],
    },
    toolbox: {
      top: '20px',
      feature: {
        dataView: {
          show: true,
          readOnly: true,
          optionToContent: (option: any): string | HTMLElement =>
            dataViewFormatter(option as EChartsOption, decimalScoreCellRenderer),
        },
        saveAsImage: {
          show: true,
          name: getTimestampedFileNameForWidget('rongo-riparian-features'),
        },
      },
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true,
    },
    xAxis: [
      {
        type: 'category',
        boundaryGap: false,
        data: xAxisDates,
      },
    ],
    yAxis: [
      {
        type: 'value',
        show: false,
      },
    ],
    series: [
      {
        name: 'Riparian Coverage',
        type: 'line',
        smooth: true,
        stack: 'total',
        lineStyle: {
          width: 0,
        },
        showSymbol: false,
        areaStyle: {
          opacity: 0.8,
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: RiparianFeaturesColours.riparianCoverageText.primary },
            { offset: 1, color: RiparianFeaturesColours.riparianCoverageText.light },
          ]),
        },
        emphasis: { focus: 'series' },
        data: riparianCoverageValues,
      },
      {
        name: 'Taonga Abundance',
        type: 'line',
        smooth: true,
        stack: 'total',
        lineStyle: {
          width: 0,
        },
        showSymbol: false,
        areaStyle: {
          opacity: 0.8,
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: RiparianFeaturesColours.taonga.primary },
            { offset: 1, color: RiparianFeaturesColours.taonga.light },
          ]),
        },
        emphasis: { focus: 'series' },
        data: taongaSpeciesAbundanceValues,
      },
      {
        name: 'Taonga Wai Abundance',
        type: 'line',
        smooth: true,
        stack: 'total',
        lineStyle: {
          width: 0,
        },
        showSymbol: false,
        areaStyle: {
          opacity: 0.8,
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: RiparianFeaturesColours.taongaWai.primary },
            { offset: 1, color: RiparianFeaturesColours.taongaWai.light },
          ]),
        },
        emphasis: { focus: 'series' },
        data: taongaWaiSpeciesAbundanceValues,
      },
      {
        name: 'Rongoa Abundance',
        type: 'line',
        smooth: true,
        stack: 'total',
        lineStyle: {
          width: 0,
        },
        showSymbol: false,
        areaStyle: {
          opacity: 0.8,
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: RiparianFeaturesColours.rongoa.primary },
            { offset: 1, color: RiparianFeaturesColours.rongoa.light },
          ]),
        },
        emphasis: { focus: 'series' },
        data: rongoaSpeciesAbundanceValues,
      },
    ],
  };
  return cfg;
};

export { getRiparianFeaturesChartConfig, RiparianFeaturesDataByDate, RiparianFeaturesData };
